import { FormNumberInput } from '@components/forms/number/base/FormNumberInput';
import { Grid, Button, Tooltip, Box } from '@mui/material';
import { useState } from 'react';
import { validatePin } from '@utils/validate';
import { PIN_LENGTH } from '@consts/location';
import { LockOpen, Lock } from '@mui/icons-material';
import { useStyles } from './styles/PinInput.styles';

import type { PinnedEntity } from '@/types/locations';

type PinInputProps<T extends PinnedEntity> = {
    draft: [T, React.Dispatch<T>];
    showLock?: boolean;
    disabled?: boolean;
    /** Grid column size (1-12). Defaults to 8 or 7 internally; set to 12 for full-width styling */
    size?: number;
};

export const generateRandomPin = () => (Math.floor(Math.random() * (9999 - 1e3)) + 1e3).toString();

export const PinInput = <T extends PinnedEntity>({ draft, showLock, disabled, size = 8 }: PinInputProps<T>) => {
    const [draftState, setDraftState] = draft;
    const [locked, setLocked] = useState(true);
    const classes = useStyles();

    const handleLockClick = () => {
        setLocked((prev) => {
            if (!prev && !validatePin(draftState.PIN)) return prev;
            return !prev;
        });
    };

    return (
        <Box
            display='flex'
            flexDirection='row'
            gap={1}
            alignItems='center'
            flex={1}
            justifyContent='center'
            ml={2}
            mt={2}>
            <FormNumberInput
                disabled={disabled || showLock ? locked : false}
                required
                size={size}
                draft={draft}
                stateKey='PIN'
                label='Pin'
                helperText={{
                    valid: '',
                    invalid: `Please provide a valid ${PIN_LENGTH} digit pin not beginning with zero.`,
                }}
                maxLength={PIN_LENGTH}
                validateFn={validatePin}
            />
            <Button
                sx={{ mt: 2 }}
                disabled={disabled || showLock ? locked : false}
                type='button'
                size={'large'}
                variant={'outlined'}
                color={'secondary'}
                onClick={() => {
                    setDraftState({
                        ...draftState,
                        PIN: generateRandomPin(),
                    });
                }}>
                Generate
            </Button>
            {showLock && (
                <Tooltip title={locked ? 'Unlock to manage pin' : 'Lock pin changes'}>
                    <Button sx={{ mt: 2 }} onClick={handleLockClick} color='inherit' disableRipple disabled={disabled}>
                        {locked ? <Lock className={classes.pointer} /> : <LockOpen className={classes.pointer} />}
                    </Button>
                </Tooltip>
            )}
        </Box>
    );
};
